import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Box, Tooltip, Chip } from '@mui/material';
import { Iconify, MultiDatePicker } from '..';
import { widgetButtonWrapperStyle } from '../../styles/widgetCounterStyle';

// Active-range chip label, e.g. "1 Jun – 30 Jun 2026". Guarded so bad values don't throw.
const formatRangeLabel = (start, end) => {
    const s = dayjs(start);
    const e = dayjs(end);
    if (!s.isValid() || !e.isValid()) return 'Date range';
    return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
};

// The Collapse/Expand-stats icon was removed 2026-07-04 (product decision): stats widgets
// are always shown. Every consumer initialized its toggle to true and nothing persisted a
// collapsed state, so all pages render expanded; the toggle/handleOpen/handleClose props
// are no longer read (callers may still pass them harmlessly until their plumbing is cleaned).
const WidgetButtons = ({ dateRangeProps, type, tableTitle, showActiveRangeChip = false, compact = false }) => {
    // The active-range chip is opt-in (showActiveRangeChip) so it only appears where a table
    // has asked for it (Treatment Started), not on every table that shares WidgetButtons.
    const rangeActive = showActiveRangeChip && Boolean(dateRangeProps?.startDate && dateRangeProps?.endDate);
    return (
        <Box sx={compact ? { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 } : widgetButtonWrapperStyle} className={tableTitle === 'Aligner Approve Leads' ? 'widgetButtonWrapper' : ''}>
            {/* Active date-range indicator + clear. Only shows while a range is applied — it
                signals the filter is active (green, matching the widget's range glow) and its
                delete (✕) resets the range via dateChange([null, null]). */}
            {rangeActive && (
                <Tooltip title='Clear date range' arrow>
                    <Chip
                        size='small'
                        icon={<Iconify icon='mdi:calendar-check' width={16} />}
                        label={formatRangeLabel(dateRangeProps.startDate, dateRangeProps.endDate)}
                        onDelete={() => dateRangeProps.dateChange([null, null])}
                        deleteIcon={<Iconify icon='mdi:close-circle' width={16} />}
                        sx={{
                            height: 28,
                            fontWeight: 600,
                            color: '#047857',
                            bgcolor: 'rgba(16, 185, 129, 0.12)',
                            border: '1px solid rgba(16, 185, 129, 0.45)',
                            '& .MuiChip-icon': { color: '#10b981' },
                            '& .MuiChip-deleteIcon': { color: 'rgba(4, 120, 87, 0.6)', '&:hover': { color: '#047857' } },
                        }}
                    />
                </Tooltip>
            )}
            {dateRangeProps && <MultiDatePicker
                value={dateRangeProps.date}
                endDate={new Date()}
                dateChange={dateRangeProps.dateChange}
                setValue={dateRangeProps.setDate}
                startDateValue={dateRangeProps.startDate}
                endDateValue={dateRangeProps.endDate}
                inputFieldType={compact ? 'icon' : 'button'}
                type={type}
            />}
        </Box>
    );
};

export default WidgetButtons;

WidgetButtons.propTypes = {
    dateRangeProps: PropTypes.object,
    type: PropTypes.string,
    tableTitle: PropTypes.string,
    // Opt-in: show the "active date range" chip + clear (✕). Off by default so only tables
    // that want it (Treatment Started) get it, not every WidgetButtons consumer.
    showActiveRangeChip: PropTypes.bool,
    // Filter-bar variant: the date picker renders as a 32px icon button (dot when a range is set).
    compact: PropTypes.bool,
};